import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Eye, Tag, ArrowLeft, Clock } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(res.data);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  useSEO({
    title: post ? `${post.meta_title || post.title} | Krieger Real Estate` : 'Blog | Krieger Real Estate',
    description: post?.meta_description || post?.excerpt || '',
    canonical: `https://joinkrieger.com/blog/${slug}`,
  });

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const readTime = (content) => {
    if (!content) return '1 min';
    const words = content.split(/\s+/).length;
    return `${Math.max(1, Math.round(words / 200))} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-28">
        <div className="max-w-3xl mx-auto px-4 animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-3/4" />
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-64 bg-slate-200 rounded-xl" />
          <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-4 bg-slate-200 rounded" />)}</div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 pt-28">
        <div className="max-w-3xl mx-auto px-4 text-center py-20">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Article Not Found</h1>
          <p className="text-slate-500 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog"><Button variant="outline" className="rounded-full">Back to Blog</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#0F1729] text-white pt-28 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 mb-6 transition-colors" data-testid="blog-back-link">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
          <div className="flex items-center gap-3 mb-4">
            {post.category && <Badge className="bg-[#002FA7]/80 text-white text-[10px] uppercase tracking-wider">{post.category}</Badge>}
            <span className="flex items-center gap-1 text-xs text-slate-400"><Calendar size={12} /> {formatDate(post.published_at)}</span>
            <span className="flex items-center gap-1 text-xs text-slate-400"><Clock size={12} /> {readTime(post.content)}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Cabinet_Grotesk'] leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.excerpt && <p className="mt-4 text-lg text-slate-400 max-w-2xl">{post.excerpt}</p>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {post.featured_image && (
          <img src={post.featured_image} alt={post.title} className="w-full rounded-xl mb-10 shadow-sm" data-testid="blog-post-image" />
        )}

        <article
          className="prose prose-slate prose-lg max-w-none
            prose-headings:font-['Cabinet_Grotesk'] prose-headings:text-slate-900
            prose-a:text-[#002FA7] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-slate-800
            prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: post.content }}
          data-testid="blog-post-content"
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={14} className="text-slate-400" />
              {post.tags.map(tag => (
                <Link key={tag} to={`/blog?tag=${tag}`}>
                  <Badge variant="outline" className="text-xs hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors cursor-pointer">
                    {tag}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Views */}
        <div className="mt-6 flex items-center gap-1 text-xs text-slate-400">
          <Eye size={12} /> {post.views || 0} views
        </div>

        {/* CTA */}
        <div className="mt-16 bg-[#0F1729] rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold font-['Cabinet_Grotesk'] text-white mb-3">Earn 90% Commission on Every Deal</h3>
          <p className="text-slate-400 text-sm mb-6">No desk fees. No monthly fees. All the tools you need.</p>
          <Link to="/apply">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0] text-white rounded-full px-8" data-testid="blog-post-cta">
              Apply Now
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
